import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Divider,
  Button,
  Chip,
  SelectChangeEvent,
  useTheme
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

export interface ColumnMapperProps {
  /**
   * En-têtes du CSV
   */
  headers: string[];
  
  /**
   * Champs requis pour le mapping
   */
  requiredFields: Array<{key: string, label: string}>;
  
  /**
   * Champs optionnels pour le mapping
   */
  optionalFields: Array<{key: string, label: string}>;
  
  /**
   * Mapping initial (optionnel)
   */
  initialMapping?: Record<string, string>;
  
  /**
   * Callback appelé lorsque le mapping change
   */
  onMappingChange: (mapping: Record<string, string>) => void;
  
  /**
   * Indique si le composant est désactivé
   * @default false
   */
  disabled?: boolean;
}

/**
 * Composant pour configurer le mapping des colonnes CSV
 */
const ColumnMapper = ({
  headers,
  requiredFields,
  optionalFields,
  initialMapping = {},
  onMappingChange,
  disabled = false
}: ColumnMapperProps): React.ReactElement => {
  const theme = useTheme();
  const [mapping, setMapping] = useState<Record<string, string>>(initialMapping);
  const [usedHeaders, setUsedHeaders] = useState<string[]>([]);
  
  // Mettre à jour les en-têtes utilisés lorsque le mapping change
  useEffect(() => {
    const used = Object.values(mapping).filter(Boolean);
    setUsedHeaders(used);
  }, [mapping]);
  
  // Mettre à jour le mapping parent lorsque le mapping local change
  useEffect(() => {
    onMappingChange(mapping);
  }, [mapping, onMappingChange]);
  
  // Gérer le changement de mapping pour un champ
  const handleMappingChange = (field: string, header: string) => {
    setMapping(prev => ({
      ...prev,
      [field]: header
    }));
  };
  
  // Vérifier si tous les champs requis sont mappés
  const allRequiredFieldsMapped = requiredFields.every(field => 
    mapping[field.key] && mapping[field.key].length > 0
  );
  
  // Réinitialiser le mapping
  const handleReset = () => {
    setMapping({});
  };
  
  // Suggérer un mapping automatique basé sur la correspondance des noms
  const suggestMapping = () => {
    const newMapping = { ...mapping };
    const allFields = [...requiredFields, ...optionalFields];
    
    allFields.forEach(field => {
      // Si le champ est déjà mappé, ne pas le modifier
      if (newMapping[field.key]) return;
      
      // Chercher une correspondance exacte ou partielle
      const lowerKey = field.key.toLowerCase();
      const lowerLabel = field.label.toLowerCase();
      
      // Essayer de trouver une correspondance
      let matchedHeader = headers.find(h => 
        h.toLowerCase() === lowerKey || 
        h.toLowerCase() === lowerLabel ||
        h.toLowerCase().includes(lowerKey) ||
        h.toLowerCase().includes(lowerLabel)
      );
      
      if (matchedHeader && !usedHeaders.includes(matchedHeader)) {
        newMapping[field.key] = matchedHeader;
      }
    });
    
    setMapping(newMapping);
  };
  
  return (
    <Box sx={{ width: '100%', mb: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Configuration du mapping des colonnes
        </Typography>
        
        <Typography variant="body2" color="text.secondary" paragraph>
          Associez chaque champ requis à une colonne de votre fichier CSV. Les champs marqués d'un astérisque (*) sont obligatoires.
        </Typography>
        
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button 
            variant="outlined" 
            onClick={handleReset}
            disabled={disabled || Object.keys(mapping).length === 0}
          >
            Réinitialiser
          </Button>
          <Button 
            variant="contained" 
            onClick={suggestMapping}
            disabled={disabled || headers.length === 0}
          >
            Suggestion automatique
          </Button>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        {/* Champs requis */}
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
          Champs obligatoires
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {requiredFields.map((field) => (
            <Grid item xs={12} sm={6} md={4} key={field.key}>
              <FormControl fullWidth disabled={disabled}>
                <InputLabel id={`${field.key}-label`} required>
                  {field.label}
                </InputLabel>
                <Select
                  labelId={`${field.key}-label`}
                  value={mapping[field.key] || ''}
                  label={`${field.label} *`}
                  onChange={(e: SelectChangeEvent) => handleMappingChange(field.key, e.target.value)}
                  error={!mapping[field.key]}
                  endAdornment={
                    mapping[field.key] ? (
                      <CheckCircleIcon 
                        sx={{ 
                          color: theme.palette.success.main,
                          mr: 1
                        }} 
                      />
                    ) : (
                      <ErrorIcon 
                        sx={{ 
                          color: theme.palette.error.main,
                          mr: 1
                        }} 
                      />
                    )
                  }
                >
                  <MenuItem value="">
                    <em>Sélectionner une colonne</em>
                  </MenuItem>
                  {headers.map((header) => (
                    <MenuItem 
                      key={header} 
                      value={header}
                      disabled={usedHeaders.includes(header) && mapping[field.key] !== header}
                    >
                      {header}
                      {usedHeaders.includes(header) && mapping[field.key] !== header && (
                        <Chip 
                          label="Utilisé" 
                          size="small" 
                          color="primary" 
                          sx={{ ml: 1, height: 20 }} 
                        />
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          ))}
        </Grid>
        
        {/* Champs optionnels */}
        {optionalFields.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Champs optionnels
            </Typography>
            
            <Grid container spacing={2}>
              {optionalFields.map((field) => (
                <Grid item xs={12} sm={6} md={4} key={field.key}>
                  <FormControl fullWidth disabled={disabled}>
                    <InputLabel id={`${field.key}-label`}>
                      {field.label}
                    </InputLabel>
                    <Select
                      labelId={`${field.key}-label`}
                      value={mapping[field.key] || ''}
                      label={field.label}
                      onChange={(e: SelectChangeEvent) => handleMappingChange(field.key, e.target.value)}
                      endAdornment={
                        mapping[field.key] ? (
                          <CheckCircleIcon 
                            sx={{ 
                              color: theme.palette.success.main,
                              mr: 1
                            }} 
                          />
                        ) : null
                      }
                    >
                      <MenuItem value="">
                        <em>Sélectionner une colonne</em>
                      </MenuItem>
                      {headers.map((header) => (
                        <MenuItem 
                          key={header} 
                          value={header}
                          disabled={usedHeaders.includes(header) && mapping[field.key] !== header}
                        >
                          {header}
                          {usedHeaders.includes(header) && mapping[field.key] !== header && (
                            <Chip 
                              label="Utilisé" 
                              size="small" 
                              color="primary" 
                              sx={{ ml: 1, height: 20 }} 
                            />
                          )}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              ))}
            </Grid>
          </>
        )}
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {allRequiredFieldsMapped 
              ? "Tous les champs obligatoires sont mappés."
              : "Veuillez mapper tous les champs obligatoires."}
          </Typography>
          
          {allRequiredFieldsMapped ? (
            <Chip 
              icon={<CheckCircleIcon />} 
              label="Prêt pour l'importation" 
              color="success" 
              variant="outlined" 
            />
          ) : (
            <Chip 
              icon={<ErrorIcon />} 
              label="Mapping incomplet" 
              color="error" 
              variant="outlined" 
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ColumnMapper;